import type { ReactNode } from "react";
import { FileText, Filter } from "lucide-react";
import { StepInfoRow } from "../StepInfoRow";
import { getString } from "./helpers";
import type { SummaryTranslator } from "./types";

export function getFilterSummary(config: Record<string, unknown>, t: SummaryTranslator): ReactNode[] {
  const items: ReactNode[] = [];

  const conditions = Array.isArray(config.conditions)
    ? (config.conditions as { field: string; op: string; value: unknown }[])
    : [];
  const logic = getString(config.logic) || "AND";

  // AND/OR are technical operators — kept verbatim
  const logicLabels: Record<string, { label: string; color: string }> = {
    AND: { label: "AND", color: "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300" },
    OR: { label: "OR", color: "bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300" },
  };
  const logicInfo = logicLabels[logic] || logicLabels.AND;

  items.push(
    <StepInfoRow
      key="logic"
      icon={<Filter className="size-3.5" />}
      label={t("summaries.filter.label_logic")}
      value={
        <span className="flex items-center gap-2">
          <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${logicInfo.color}`}>
            {logicInfo.label}
          </span>
          <span className="text-[10px] text-gray-400">{t("summaries.filter.conditions_count", { count: conditions.length })}</span>
        </span>
      }
    />
  );

  const maxDisplay = 4;
  const unaryOps = ["IS NULL", "IS NOT NULL", "IS EMPTY", "IS NOT EMPTY"];
  conditions.slice(0, maxDisplay).forEach((c, i) => {
    const isUnary = unaryOps.includes(c.op);
    const valueStr = isUnary ? "" : ` ${typeof c.value === "string" ? `"${c.value}"` : String(c.value ?? "")}`;
    items.push(
      <StepInfoRow key={`cond-${i}`} icon={<FileText className="size-3.5" />} label={c.field} value={`${c.op}${valueStr}`} mono />
    );
  });

  if (conditions.length > maxDisplay) {
    items.push(
      <div key="more" className="pl-6 text-[10px] text-gray-400 dark:text-gray-500">
        {t("summaries.filter.more_conditions", { count: conditions.length - maxDisplay })}
      </div>
    );
  }

  return items;
}
